import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { ShieldCheck, ShieldOff } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  ControlPlaneError,
  createSuperadminGrant,
  deleteSuperadminGrant,
  listSuperadminGrants,
  newRequestId,
  withStepUp,
  type SuperadminGrant,
} from '@/services/controlPlane/superadmin'

interface Props {
  /** L3 User id — the same CUID as the Control Plane identity id. */
  identityId: string
  /** Projected `User.isSuperadmin`; refreshed by the parent after a change. */
  projectedIsSuperadmin: boolean
  onChanged: () => Promise<void>
}

/**
 * Grant or revoke Superadmin for one identity. The Control Plane is the
 * authority: the current grant is read live, every change is step-up
 * confirmed, and the projected flag on the User row follows via the outbox.
 */
export function SuperadminGrantCard({ identityId, projectedIsSuperadmin, onChanged }: Props) {
  const [grant, setGrant] = useState<SuperadminGrant | null | undefined>(undefined)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try {
      const grants = await listSuperadminGrants()
      setGrant(grants.find(g => g.identityId === identityId) ?? null)
      setLoadError(null)
    } catch (error) {
      setGrant(null)
      setLoadError(error instanceof Error ? error.message : 'Failed to load grants')
    }
  }, [identityId])

  useEffect(() => {
    void load()
  }, [load])

  const change = async () => {
    const trimmed = reason.trim()
    if (!trimmed) {
      toast.error('A reason is required; it is recorded on the audit event.')
      return
    }
    setBusy(true)
    const requestId = newRequestId()
    const onConfirm = () => toast.info('Confirm your Google account in the popup to continue.')
    try {
      if (grant) {
        await withStepUp(
          () => deleteSuperadminGrant({ grantId: grant.id, reason: trimmed, requestId }),
          onConfirm,
        )
        toast.success('Superadmin revoked')
      } else {
        await withStepUp(
          () => createSuperadminGrant({ identityId, reason: trimmed, requestId }),
          onConfirm,
        )
        toast.success('Superadmin granted')
      }
      setReason('')
      await load()
      await onChanged()
    } catch (error) {
      if (error instanceof ControlPlaneError && error.reason === 'CONFIRMATION_REQUIRED') {
        toast.error('Confirmation was not completed. Try the change again.')
      } else {
        toast.error(error instanceof Error ? error.message : 'Failed to update superadmin')
      }
    } finally {
      setBusy(false)
    }
  }

  const syncing = grant !== undefined && Boolean(grant) !== projectedIsSuperadmin

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">Superadmin</CardTitle>
        {grant ? (
          <ShieldCheck className="h-4 w-4 text-red-600" />
        ) : (
          <ShieldOff className="h-4 w-4 text-muted-foreground" />
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          {grant === undefined ? (
            <span className="text-sm text-gray-500">Checking grant…</span>
          ) : grant ? (
            <Badge variant="error">active grant</Badge>
          ) : (
            <Badge variant="default">no grant</Badge>
          )}
          {syncing && <span className="text-xs text-gray-500">projection syncing…</span>}
        </div>
        {grant?.grantReason && (
          <p className="text-xs text-gray-500">Reason on record: {grant.grantReason}</p>
        )}
        {loadError && <p className="text-xs text-red-600">{loadError}</p>}
        <Input
          placeholder={grant ? 'Reason for revoking' : 'Reason for granting'}
          value={reason}
          onChange={e => setReason(e.target.value)}
          disabled={busy || grant === undefined}
        />
        <Button
          variant={grant ? 'destructive' : 'default'}
          size="sm"
          onClick={() => void change()}
          disabled={busy || grant === undefined || !reason.trim()}
        >
          {busy ? 'Working…' : grant ? 'Revoke superadmin' : 'Grant superadmin'}
        </Button>
      </CardContent>
    </Card>
  )
}
